import { DashboardEconomyAsOfTotals } from '@interfaces/dashboard/responses/dashboard-economy-as-of.response';

/** Economia somada das empresas selecionadas numa data (card 3323). */
export interface EconomyAsOfSum {
  projectedSavingsAsOf: number | null;
  contractualSavingsToDate: number | null;
  actualSavingsToDate: number | null;
  activeLoanCount: number;
}

/** Variação entre o início e o fim do período: Prevista Inicial e as duas réguas "até a data". */
export interface EconomyPeriodDelta {
  /** Quanto a Prevista Inicial (projeção até o vencimento) mudou entre o início e o fim do período. */
  projected: number | null;
  contractual: number | null;
  actual: number | null;
}

/**
 * Soma os totais de cada empresa. Um campo só fica nulo quando TODAS as empresas vieram sem ele
 * (nulo não é zero: empresa sem cálculo não pode puxar a soma para baixo em silêncio).
 */
export function sumEconomyAsOf(
  totals: (DashboardEconomyAsOfTotals | null | undefined)[]
): EconomyAsOfSum {
  const present = totals.filter((t): t is DashboardEconomyAsOfTotals => !!t);
  const sumField = (pick: (t: DashboardEconomyAsOfTotals) => number | null): number | null => {
    const values = present.map(pick).filter((v): v is number => v !== null && v !== undefined);
    return values.length ? values.reduce((acc, v) => acc + v, 0) : null;
  };

  return {
    projectedSavingsAsOf: sumField(t => t.projectedSavingsAsOf),
    contractualSavingsToDate: sumField(t => t.contractualSavingsToDate),
    actualSavingsToDate: sumField(t => t.actualSavingsToDate),
    activeLoanCount: present.reduce((acc, t) => acc + (t.activeLoanCount ?? 0), 0),
  };
}

/**
 * Economia gerada dentro do período: "até o fim" menos "até o início", em cada régua. Sem início
 * (período aberto) não há variação; régua nula em qualquer ponta fica nula.
 */
export function economyPeriodDelta(
  end: EconomyAsOfSum | null,
  start: EconomyAsOfSum | null
): EconomyPeriodDelta | null {
  if (!end || !start) return null;
  const diff = (a: number | null, b: number | null): number | null =>
    a === null || b === null ? null : a - b;
  return {
    projected: diff(end.projectedSavingsAsOf, start.projectedSavingsAsOf),
    contractual: diff(end.contractualSavingsToDate, start.contractualSavingsToDate),
    actual: diff(end.actualSavingsToDate, start.actualSavingsToDate),
  };
}
